import {
  AdVariationData,
  CarouselCard,
  FacebookAdMockupProps,
  GoogleSearchAdMockupProps,
  GoogleDisplayAdMockupProps,
  GoogleShoppingAdMockupProps,
  InstagramAdMockupProps,
  YouTubeAdMockupProps,
  TikTokAdMockupProps,
} from '../types';
import { normalizeFilename, extractMediaTags } from '@/utils/mediaTagParser';

/**
 * Extracts MEDIA tags from an ad variation's text fields
 * If ad.media_tags is already present, returns it
 * Otherwise, searches all text fields for [MEDIA:...] tags
 */
const getMediaTagsFromAd = (ad: AdVariationData): string[] => {
  // If media_tags already populated, use it
  if (ad.media_tags && ad.media_tags.length > 0) {
    return ad.media_tags;
  }

  // Otherwise, extract from text fields
  const allText: string[] = [];

  if (ad.headlines && Array.isArray(ad.headlines)) {
    allText.push(...ad.headlines);
  }

  if (ad.primary_text) {
    if (Array.isArray(ad.primary_text)) {
      allText.push(...ad.primary_text);
    } else {
      allText.push(ad.primary_text);
    }
  }

  if (ad.descriptions && Array.isArray(ad.descriptions)) {
    allText.push(...ad.descriptions);
  }

  if (ad.description && typeof ad.description === 'string') {
    allText.push(ad.description);
  }

  if (ad.caption && typeof ad.caption === 'string') {
    allText.push(ad.caption);
  }

  // Extract all MEDIA tags from collected text
  const tags: string[] = [];
  for (const text of allText) {
    const extracted = extractMediaTags(text);
    for (const tag of extracted) {
      tags.push(tag.fullMatch);
    }
  }

  return tags;
};

/**
 * Extracts media URL from conversation media by filename
 * Supports pre-resolved media URLs from backend
 * Uses normalized filename matching to handle spacing and case differences
 */
export const extractMediaUrl = (
  mediaTags: string[] | undefined,
  conversationMedia: Array<{ filename: string; url: string; type: string }>,
  resolvedMedia?: Array<{ filename: string; url: string; }>
): string | undefined => {
  // Prefer pre-resolved media from backend (from resolved_media array)
  if (resolvedMedia && resolvedMedia.length > 0) {
    return resolvedMedia[0].url;
  }

  // Use provided media tags or empty array
  const tagsToUse = mediaTags || [];

  if (!tagsToUse || tagsToUse.length === 0) {
    return undefined;
  }

  // Extract filename from first media tag: [MEDIA:beach.jpg|type=image|placement=hero]
  const match = tagsToUse[0].match(/\[MEDIA:([^\|]+)\|/);
  if (!match) {
    return undefined;
  }

  const filename = match[1];
  const normalizedLookupFilename = normalizeFilename(filename);

  // Try normalized filename matching (handles spacing and case differences)
  const media = conversationMedia.find(m =>
    normalizeFilename(m.filename) === normalizedLookupFilename
  );

  if (!media) {
    return undefined;
  }

  return media.url;
};

/**
 * Maps ad variation data to Facebook mockup props
 */
export const mapToFacebookMockup = (
  ad: AdVariationData,
  conversationMedia: Array<{ filename: string; url: string; type: string }>
): FacebookAdMockupProps => {
  // Convert primary_text to array if it's a string
  const primaryTexts = Array.isArray(ad.primary_text)
    ? ad.primary_text
    : ad.primary_text
    ? [ad.primary_text]
    : [];

  // Convert descriptions to array if needed
  const descriptions = ad.descriptions || (ad.description ? [ad.description] : []);

  // Extract media tags from text fields if not explicitly provided
  const mediaTags = ad.media_tags || getMediaTagsFromAd(ad);

  return {
    headlines: ad.headlines || [],
    primaryTexts: primaryTexts,
    descriptions: descriptions,
    mediaUrl: extractMediaUrl(mediaTags, conversationMedia),
    callToAction: ad.call_to_action || 'Learn More',
    linkUrl: ad.link_url || 'yourbrand.com',
    brandName: 'Your Brand',
    showEngagement: true,
  };
};

/**
 * Maps ad variation data to Instagram mockup props
 */
export const mapToInstagramMockup = (
  ad: AdVariationData,
  conversationMedia: Array<{ filename: string; url: string; type: string }>
): InstagramAdMockupProps => {
  // Build array of caption variations
  const captions = [];
  if (ad.caption) captions.push(ad.caption);
  if (ad.headlines && ad.headlines.length > 0) {
    ad.headlines.forEach(h => captions.push(h));
  }
  if (ad.primary_text) {
    const primaryTexts = Array.isArray(ad.primary_text) ? ad.primary_text : [ad.primary_text];
    primaryTexts.forEach(t => captions.push(t));
  }

  // Extract media tags from text fields if not explicitly provided
  const mediaTags = ad.media_tags || getMediaTagsFromAd(ad);

  return {
    imageUrl: extractMediaUrl(mediaTags, conversationMedia),
    captions: captions.length > 0 ? captions : undefined,
    callToAction: ad.call_to_action || 'Learn More',
    brandName: 'your_brand',
  };
};

/**
 * Maps ad variation data to Google Search mockup props
 */
export const mapToGoogleSearchMockup = (
  ad: AdVariationData
): GoogleSearchAdMockupProps => {
  return {
    headlines: ad.headlines || [],
    descriptions: ad.descriptions || [],
    finalUrl: ad.final_url || ad.link_url || 'https://yourbrand.com',
    displayPath: ad.display_path,
  };
};

/**
 * Maps ad variation data to YouTube mockup props
 */
export const mapToYouTubeMockup = (
  ad: AdVariationData,
  conversationMedia: Array<{ filename: string; url: string; type: string }>
): YouTubeAdMockupProps => {
  // Convert descriptions to array if needed
  const descriptions = ad.descriptions || (ad.description ? [ad.description] : []);

  // Extract media tags from text fields if not explicitly provided
  const mediaTags = ad.media_tags || getMediaTagsFromAd(ad);

  return {
    thumbnailUrl: extractMediaUrl(mediaTags, conversationMedia),
    headlines: ad.headlines || [],
    descriptions: descriptions,
    callToAction: ad.call_to_action || 'Learn More',
    brandName: 'Your Brand',
    skippable: true,
  };
};

/**
 * Maps ad variation data to TikTok mockup props
 */
export const mapToTikTokMockup = (
  ad: AdVariationData,
  conversationMedia: Array<{ filename: string; url: string; type: string }>
): TikTokAdMockupProps => {
  // Build array of caption variations
  const captions = [];
  if (ad.caption) captions.push(ad.caption);
  if (ad.headlines && ad.headlines.length > 0) {
    ad.headlines.forEach(h => captions.push(h));
  }
  if (ad.primary_text) {
    const primaryTexts = Array.isArray(ad.primary_text) ? ad.primary_text : [ad.primary_text];
    primaryTexts.forEach(t => captions.push(t));
  }

  // Extract media tags from text fields if not explicitly provided
  const mediaTags = ad.media_tags || getMediaTagsFromAd(ad);

  return {
    videoUrl: extractMediaUrl(mediaTags, conversationMedia),
    captions: captions.length > 0 ? captions : undefined,
    callToAction: ad.call_to_action || 'Learn More',
    brandName: 'yourbrand',
  };
};

/**
 * Maps ad variation data to Pinterest mockup props
 */
export const mapToPinterestMockup = (
  ad: AdVariationData,
  conversationMedia: Array<{ filename: string; url: string; type: string }>
) => {
  // Extract media tags from text fields if not explicitly provided
  const mediaTags = ad.media_tags || getMediaTagsFromAd(ad);
  const mediaUrl = extractMediaUrl(mediaTags, conversationMedia);
  const isVideo = mediaUrl && mediaUrl.match(/\.(mp4|webm|ogg|mov|avi)$/i);

  // Pinterest uses title and description
  const title = ad.headlines?.[0] || ad.title || 'Your Pin Title';
  const description = ad.primary_text
    ? (Array.isArray(ad.primary_text) ? ad.primary_text[0] : ad.primary_text)
    : (ad.description || ad.descriptions?.[0] || '');

  return {
    imageUrl: isVideo ? undefined : mediaUrl,
    videoUrl: isVideo ? mediaUrl : undefined,
    title,
    description,
    callToAction: ad.call_to_action || 'Learn More',
    brandName: 'Your Brand',
    boardName: 'Saved to Board',
    linkUrl: ad.link_url || ad.final_url || undefined,
  };
};

/**
 * Maps ad variation data to Twitter mockup props
 */
export const mapToTwitterMockup = (
  ad: AdVariationData,
  conversationMedia: Array<{ filename: string; url: string; type: string }>
) => {
  // Extract media tags from text fields if not explicitly provided
  const mediaTags = ad.media_tags || getMediaTagsFromAd(ad);
  const mediaUrl = extractMediaUrl(mediaTags, conversationMedia);
  const isVideo = mediaUrl && mediaUrl.match(/\.(mp4|webm|ogg|mov|avi)$/i);

  // Twitter uses tweet text (primary_text or concatenated headlines)
  let tweetText = '';
  if (ad.primary_text) {
    tweetText = Array.isArray(ad.primary_text) ? ad.primary_text[0] : ad.primary_text;
  } else if (ad.headlines && ad.headlines.length > 0) {
    tweetText = ad.headlines.join('. ');
  } else if (ad.description) {
    tweetText = ad.description;
  } else if (ad.descriptions && ad.descriptions.length > 0) {
    tweetText = ad.descriptions[0];
  }

  return {
    imageUrl: isVideo ? undefined : mediaUrl,
    videoUrl: isVideo ? mediaUrl : undefined,
    tweetText: tweetText || 'Your promoted tweet content here',
    callToAction: ad.call_to_action || 'Learn more',
    brandName: 'Your Brand',
    brandHandle: '@yourbrand',
    linkUrl: ad.link_url || ad.final_url || undefined,
    verified: false,
  };
};

/**
 * Maps ad variation data to Carousel mockup props
 * Extracts multiple media items and creates carousel cards
 * Works across multiple platforms (Facebook, Instagram, Pinterest)
 */
export const mapToCarouselMockup = (
  ad: AdVariationData,
  conversationMedia: Array<{ filename: string; url: string; type: string }>,
  platform: string
) => {
  const resolveUrl = (tag: string | undefined): { image?: string; video?: string } => {
    if (!tag) return {};
    const filename = tag.match(/\[MEDIA:([^\|]+)\|/)?.[1] || '';
    const mediaUrl = conversationMedia.find(m =>
      normalizeFilename(m.filename) === normalizeFilename(filename)
    )?.url;
    if (!mediaUrl) return {};
    return mediaUrl.match(/\.(mp4|webm|ogg|mov|avi)$/i)
      ? { video: mediaUrl }
      : { image: mediaUrl };
  };

  let cards: CarouselCard[];

  if (ad.cards && ad.cards.length > 0) {
    // New schema: each card has its own media_tag, headline, description, link_url
    cards = ad.cards.map((card, index) => ({
      id: `card-${index}`,
      ...resolveUrl(card.media_tag),
      headline: card.headline || '',
      description: card.description || '',
      callToAction: ad.call_to_action || 'Learn More',
      linkUrl: card.link_url || ad.link_url || ad.final_url || 'yourbrand.com',
    }));
  } else {
    // Legacy fallback: extract MEDIA tags from media_tags or text fields
    const mediaTags = ad.media_tags || getMediaTagsFromAd(ad);
    cards = mediaTags.map((tag, index) => ({
      id: `card-${index}`,
      ...resolveUrl(tag),
      headline: ad.headlines?.[index % (ad.headlines?.length || 1)] || '',
      description: ad.primary_text
        ? (Array.isArray(ad.primary_text) ? ad.primary_text[index % ad.primary_text.length] : ad.primary_text)
        : '',
      callToAction: ad.call_to_action || 'Learn More',
      linkUrl: ad.link_url || ad.final_url || 'yourbrand.com',
    }));
  }

  // Platform-specific configuration
  const platformConfig = {
    facebook: { maxCards: 5, cardAspectRatio: '1:1' as const },
    meta: { maxCards: 5, cardAspectRatio: '1:1' as const },
    instagram: { maxCards: 10, cardAspectRatio: '1:1' as const },
    pinterest: { maxCards: 15, cardAspectRatio: '4:5' as const },
  };

  const config = platformConfig[platform as keyof typeof platformConfig] || platformConfig.facebook;

  return {
    cards: cards.length > 0 ? cards : [{
      id: 'empty',
      headline: 'No carousel images',
      description: 'Please add media files to the campaign',
      callToAction: ad.call_to_action || 'Learn More',
    }],
    platform: (platform === 'meta' ? 'meta' : platform) as 'facebook' | 'instagram' | 'pinterest' | 'meta',
    brandName: 'Your Brand',
    maxCards: config.maxCards,
    cardAspectRatio: config.cardAspectRatio,
    showIndicators: true,
  };
};

/**
 * Maps ad variation data to Google Display mockup props
 */
export const mapToGoogleDisplayMockup = (
  ad: AdVariationData,
  conversationMedia: Array<{ filename: string; url: string; type: string }>
): GoogleDisplayAdMockupProps => {
  const mediaTags = ad.media_tags || getMediaTagsFromAd(ad);

  return {
    imageUrl: extractMediaUrl(mediaTags, conversationMedia) || '',
    headline: ad.headlines?.[0] || ad.title || 'Your Headline',
    description: ad.descriptions?.[0] || ad.description || '',
    callToAction: ad.call_to_action || 'Learn More',
    brandName: 'Your Brand',
  };
};

/**
 * Maps ad variation data to Google Shopping mockup props
 */
export const mapToGoogleShoppingMockup = (
  ad: AdVariationData,
  conversationMedia: Array<{ filename: string; url: string; type: string }>
): GoogleShoppingAdMockupProps => {
  const mediaTags = ad.media_tags || getMediaTagsFromAd(ad);

  return {
    productTitle: ad.headlines?.[0] || ad.title || 'Product Title',
    price: ad.price || '$0.00',
    storeName: 'Your Store',
    imageUrl: extractMediaUrl(mediaTags, conversationMedia),
  };
};
